import Vue from 'vue';
import Vuex from 'vuex';
import { getMe } from '../api/me';
import { getTaskRedDot } from '../api/tasks';
import { adminGetBadges } from '../api/admin';
import { handlerGetBadges } from '../api/handler';
import { secondaryGetBadges } from '../api/secondary';
import { caseStats, getCaseRedDot } from '../api/tickets';
import { ensureCsrf, logout as apiLogout } from '../api/auth';

Vue.use(Vuex);

const APP_BUILD_ID = process.env.VUE_APP_BUILD_ID || 'dev';
const APP_BUILD_STORAGE_KEY = 'ticket_app_build_id';

export default new Vuex.Store({
  state: {
    user: null,
    mustConfirmAnnouncement: false,
    unconfirmedAnnouncementId: null,
    // When an announcement gate forces navigation, remember where to return after confirm.
    announcementReturnTo: localStorage.getItem('announcementReturnTo') || '',
    taskRedDotCount: 0,
    ticketRedDotCount: 0,
    ticketNeedMoreCount: 0,
    ticketClosedNoticeCount: 0,
    adminBadges: {
      announcements: 0,
      tasks: 0
    },
    handlerBadges: {
      pool: 0,
      disputes: 0,
      poolReminder: 0,
      inProgressReminder: 0,
      disputeReminder: 0
    },
    secondaryBadges: {
      pendingCases: 0,
      submittedCases: 0,
      closedNotice: 0,
      secondaryDisputes: 0,
      pendingReminder: 0,
      submittedReminder: 0,
      disputeReminder: 0,
      taskRedDot: 0,
      mustConfirmAnnouncement: false
    },
    // Bump counters to trigger "online refresh" without full page reload.
    versions: {
      tasks: 0,
      announcements: 0,
      userCases: 0,
      handlerCases: 0,
      secondaryCases: 0,
      disputes: 0
    }
  },
  getters: {
    // Auth state is derived from /api/me (cookie-based session), not from a JS-visible token.
    isAuthed: (s) => !!s.user,
    role: (s) => (s.user ? s.user.role : null)
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setAnnouncementGate(state, payload) {
      state.mustConfirmAnnouncement = !!payload.mustConfirmAnnouncement;
      state.unconfirmedAnnouncementId = payload.unconfirmedAnnouncementId || null;
    },
    setAnnouncementReturnTo(state, path) {
      if (!path) return;
      if (state.announcementReturnTo) return;
      if (path === '/login' || path === '/announcements/confirm') return;
      state.announcementReturnTo = path;
      localStorage.setItem('announcementReturnTo', path);
    },
    clearAnnouncementReturnTo(state) {
      state.announcementReturnTo = '';
      localStorage.removeItem('announcementReturnTo');
    },
    setTaskRedDotCount(state, count) {
      state.taskRedDotCount = Number(count || 0);
    },
    setTicketRedDotCount(state, count) {
      state.ticketRedDotCount = Number(count || 0);
    },
    setTicketNeedMoreCount(state, count) {
      state.ticketNeedMoreCount = Number(count || 0);
    },
    setTicketClosedNoticeCount(state, count) {
      state.ticketClosedNoticeCount = Number(count || 0);
    },
    setAdminBadges(state, payload) {
      const p = payload || {};
      state.adminBadges = {
        announcements: Number(p.announcements || 0),
        tasks: Number(p.tasks || 0)
      };
    },
    setHandlerBadges(state, payload) {
      const p = payload || {};
      state.handlerBadges = {
        pool: Number(p.pool || 0),
        disputes: Number(p.disputes || 0),
        poolReminder: Number(p.poolReminder || 0),
        inProgressReminder: Number(p.inProgressReminder || 0),
        disputeReminder: Number(p.disputeReminder || 0)
      };
    },
    setSecondaryBadges(state, payload) {
      const p = payload || {};
      state.secondaryBadges = {
        pendingCases: Number(p.pendingCases || 0),
        submittedCases: Number(p.submittedCases || p.returnedCases || 0),
        closedNotice: Number(p.closedNotice || p.mySubmittedNotice || 0),
        secondaryDisputes: Number(p.secondaryDisputes || 0),
        pendingReminder: Number(p.pendingReminderTotal || p.pendingReminder || 0),
        submittedReminder: Number(p.submittedReminderTotal || p.submittedReminder || 0),
        disputeReminder: Number(p.disputeReminderTotal || p.disputeReminder || 0),
        taskRedDot: Number(p.taskRedDot || 0),
        mustConfirmAnnouncement: !!p.mustConfirmAnnouncement
      };
    },
    bumpVersion(state, key) {
      if (!key) return;
      if (!state.versions[key] && state.versions[key] !== 0) return;
      state.versions[key] = state.versions[key] + 1;
    }
  },
  actions: {
    async bootstrap({ dispatch, commit, state }) {
      // Remove legacy localStorage token if present (we now use HttpOnly cookie auth).
      try {
        localStorage.removeItem('token');
      } catch (e) {}

      try {
        const previousBuild = localStorage.getItem(APP_BUILD_STORAGE_KEY);
        if (previousBuild && previousBuild !== APP_BUILD_ID) {
          await apiLogout().catch(() => {});
          localStorage.setItem(APP_BUILD_STORAGE_KEY, APP_BUILD_ID);
          commit('setUser', null);
          commit('setAnnouncementGate', { mustConfirmAnnouncement: false, unconfirmedAnnouncementId: null });
          commit('clearAnnouncementReturnTo');
          throw new Error('APP_UPDATED');
        }
        localStorage.setItem(APP_BUILD_STORAGE_KEY, APP_BUILD_ID);
      } catch (e) {
        if (e && e.message === 'APP_UPDATED') throw e;
      }

      // Ensure CSRF cookie exists before any state-changing requests.
      try {
        await ensureCsrf();
      } catch (e) {
        // ignore
      }

      await dispatch('refreshMe');
      await dispatch('refreshTaskRedDot');
      await dispatch('refreshTicketRedDot');
      await dispatch('refreshTicketNeedMore');
      await dispatch('refreshAdminBadges');
      await dispatch('refreshHandlerBadges');
      await dispatch('refreshSecondaryBadges');
    },
    async refreshMe({ commit }) {
      const me = await getMe();
      commit('setUser', me);
      commit('setAnnouncementGate', me);
    },
    async refreshTaskRedDot({ commit, state }) {
      // Task red-dot is defined only for USER (Task receipts belong to user-side).
      if (!state.user) return;
      if (state.user.role && state.user.role !== 'USER') return;
      try {
        const r = await getTaskRedDot();
        commit('setTaskRedDotCount', r.count);
      } catch (e) {
        // ignore
      }
    },
    async refreshTicketRedDot({ commit, state }) {
      if (!state.user) return;
      if (state.user.role && state.user.role !== 'USER') return;
      try {
        const r = await getCaseRedDot();
        commit('setTicketRedDotCount', r.count || 0);
      } catch (e) {
        // ignore
      }
    },
    async refreshTicketNeedMore({ commit, state }) {
      if (!state.user) return;
      if (state.user.role && state.user.role !== 'USER') return;
      try {
        const r = await caseStats();
        commit('setTicketNeedMoreCount', r.waitingResubmit || 0);
        commit('setTicketClosedNoticeCount', (r.closedNotice || 0) + (r.disputeDecisionNotice || 0));
      } catch (e) {
        // ignore
      }
    },
    async refreshAdminBadges({ commit, state }) {
      if (!state.user || state.user.role !== 'ADMIN') return;
      if (state.mustConfirmAnnouncement) return;
      try {
        const r = await adminGetBadges();
        commit('setAdminBadges', { announcements: r.announcementNewTotal, tasks: r.taskNewTotal });
      } catch (e) {
        // ignore
      }
    },
    async refreshHandlerBadges({ commit, state }) {
      if (!state.user || state.user.role !== 'HANDLER') return;
      if (state.mustConfirmAnnouncement) return;
      try {
        const r = await handlerGetBadges();
        commit('setHandlerBadges', {
          pool: r.poolTotal || 0,
          disputes: r.disputePoolTotal || 0,
          poolReminder: r.poolReminderTotal || 0,
          inProgressReminder: r.inProgressReminderTotal || 0,
          disputeReminder: r.disputeReminderTotal || 0
        });
      } catch (e) {
        // ignore
      }
    },
    async refreshSecondaryBadges({ commit, state }) {
      if (!state.user || state.user.role !== 'SECONDARY_HANDLER') return;
      if (state.mustConfirmAnnouncement) return;
      try {
        const r = await secondaryGetBadges();
        commit('setSecondaryBadges', r || {});
      } catch (e) {
        // ignore
      }
    },
    async logout({ commit }, opts) {
      const remote = !(opts && opts.remote === false);
      if (remote) {
        try {
          await apiLogout();
        } catch (e) {
          // ignore
        }
      }
      commit('setUser', null);
      commit('setAnnouncementGate', { mustConfirmAnnouncement: false, unconfirmedAnnouncementId: null });
      commit('clearAnnouncementReturnTo');
      commit('setTaskRedDotCount', 0);
      commit('setTicketRedDotCount', 0);
      commit('setTicketNeedMoreCount', 0);
      commit('setTicketClosedNoticeCount', 0);
      commit('setAdminBadges', { announcements: 0, tasks: 0 });
      commit('setHandlerBadges', { pool: 0, disputes: 0, poolReminder: 0, inProgressReminder: 0, disputeReminder: 0 });
      commit('setSecondaryBadges', { pendingCases: 0, submittedCases: 0, closedNotice: 0, secondaryDisputes: 0, pendingReminder: 0, submittedReminder: 0, disputeReminder: 0, taskRedDot: 0, mustConfirmAnnouncement: false });
    }
  }
});
